import { isClerkAPIResponseError } from '@clerk/nextjs/errors'
import { useTranslation } from 'next-i18next'

/**
 * Maps Clerk error codes to translation keys
 * Error codes are grouped by category to reduce translation file size
 * Based on: https://clerk.com/docs/guides/development/errors/frontend-api
 */
const clerkErrorCodeMap: Record<string, string> = {
  // Authentication errors
  invalid_authentication: 'errors.clerk.invalidCredentials',
  invalid_authorization: 'errors.clerk.unauthorized',
  session_exists: 'errors.clerk.sessionExists',
  session_not_found: 'errors.clerk.sessionNotFound',
  signed_out: 'errors.clerk.signedOut',
  invalid_authorization_header_format: 'errors.clerk.invalidFormat',
  invalid_csrf_token: 'errors.clerk.invalidFormat',
  invalid_handshake: 'errors.clerk.invalidFormat',
  invalid_host: 'errors.clerk.invalidFormat',
  invalid_origin_header: 'errors.clerk.invalidFormat',
  invalid_request_for_environment: 'errors.clerk.invalidFormat',
  invalid_user_settings: 'errors.clerk.invalidValue',
  missing_request_headers_for_non_standard_browsers: 'errors.clerk.invalidFormat',
  missing_request_headers_for_standard_browsers: 'errors.clerk.invalidFormat',
  multiple_authorization_header_values: 'errors.clerk.invalidFormat',
  multiple_origin_header_values: 'errors.clerk.invalidFormat',
  native_api_disabled: 'errors.clerk.featureDisabled',
  origin_and_authorization_mutually_exclusive: 'errors.clerk.invalidFormat',
  url_based_session_syncing_disabled: 'errors.clerk.featureDisabled',
  identification_exists: 'errors.clerk.identifierExists',
  identifier_contains_subaddresses: 'errors.clerk.invalidValue',
  identifier_from_blocked_country_code: 'errors.clerk.unauthorized',
  identifier_not_allowed_access: 'errors.clerk.unauthorized',
  subaddress_restriction_email_already_used: 'errors.clerk.identifierExists',
  unsupported_country: 'errors.clerk.invalidFormat',

  // Form validation errors - grouped by type
  form_parameter_invalid_format: 'errors.clerk.invalidFormat',
  form_parameter_invalid_value: 'errors.clerk.invalidValue',
  form_parameter_invalid_url: 'errors.clerk.invalidUrl',
  form_parameter_invalid_email_address: 'errors.clerk.invalidEmail',
  form_parameter_invalid_phone_number: 'errors.clerk.invalidPhone',
  form_parameter_missing: 'errors.clerk.missingField',
  form_parameter_max_length_exceeded: 'errors.clerk.maxLengthExceeded',
  form_invalid_email_address: 'errors.clerk.invalidEmail',
  form_invalid_email_addresses: 'errors.clerk.invalidEmail',
  form_invalid_phone_number: 'errors.clerk.invalidPhone',
  form_invalid_parameter_format: 'errors.clerk.invalidFormat',
  form_invalid_parameter_value: 'errors.clerk.invalidValue',
  form_param_format_invalid: 'errors.clerk.invalidFormat',
  form_disabled_parameter_value: 'errors.clerk.invalidValue',
  form_duplicate_parameter: 'errors.clerk.invalidValue',
  form_identifier_not_found: 'errors.clerk.invalidCredentials',
  form_identifier_exists: 'errors.clerk.identifierExists',
  form_incorrect_code: 'errors.clerk.verificationFailed',
  form_incorrect_signature: 'errors.clerk.verificationFailed',
  form_invalid_encoding_parameter_value: 'errors.clerk.invalidValue',
  form_invalid_parameter_value_with_allowed: 'errors.clerk.invalidValue',
  form_invalid_type_parameter: 'errors.clerk.invalidFormat',
  form_invalid_username_character: 'errors.clerk.invalidFormat',
  form_invalid_username_length: 'errors.clerk.invalidValue',
  form_invalid_username_needs_non_number_char_code: 'errors.clerk.invalidFormat',
  form_invalid_web3_wallet_address: 'errors.clerk.invalidValue',
  form_legal_not_accepted: 'errors.clerk.invalidValue',
  form_maximum_parameters_exceeded: 'errors.clerk.maxLengthExceeded',
  form_meta: 'errors.clerk.invalidValue',
  form_missing: 'errors.clerk.missingField',
  form_password_does_not_match: 'errors.clerk.passwordNoMatch',
  form_password_size_in_bytes_exceeded: 'errors.clerk.passwordTooLong',
  form_param_none_not_supported: 'errors.clerk.invalidValue',
  form_param_size_in_bytes_too_large: 'errors.clerk.maxLengthExceeded',

  // Password errors
  form_password_length_too_short: 'errors.clerk.passwordTooShort',
  form_password_length_too_long: 'errors.clerk.passwordTooLong',
  form_password_pwned: 'errors.clerk.passwordCompromised',
  form_password_incorrect: 'errors.clerk.invalidCredentials',
  form_password_not_strong_enough: 'errors.clerk.passwordNotStrong',
  form_password_no_uppercase: 'errors.clerk.passwordNoUppercase',
  form_password_no_lowercase: 'errors.clerk.passwordNoLowercase',
  form_password_no_number: 'errors.clerk.passwordNoNumber',
  form_password_no_special_char: 'errors.clerk.passwordNoSpecialChar',
  no_password_set: 'errors.clerk.noPasswordSet',
  form_invalid_password_length_too_short: 'errors.clerk.passwordTooShort',
  form_invalid_password_length_too_long: 'errors.clerk.passwordTooLong',
  form_invalid_password_not_strong_enough: 'errors.clerk.passwordNotStrong',
  form_invalid_password_no_uppercase: 'errors.clerk.passwordNoUppercase',
  form_invalid_password_no_lowercase: 'errors.clerk.passwordNoLowercase',
  form_invalid_password_no_number: 'errors.clerk.passwordNoNumber',
  form_invalid_password_no_special_char: 'errors.clerk.passwordNoSpecialChar',
  password_required: 'errors.clerk.missingField',

  // Verification errors
  verification_failed: 'errors.clerk.verificationFailed',
  verification_expired: 'errors.clerk.verificationExpired',
  verification_not_sent: 'errors.clerk.verificationNotSent',
  verification_already_verified: 'errors.clerk.verificationFailed',
  verification_code_too_many_requests: 'errors.clerk.tooManyAttempts',
  verification_invalid_link_token: 'errors.clerk.verificationFailed',
  verification_invalid_link_token_source: 'errors.clerk.verificationFailed',
  verification_invalid_strategy: 'errors.clerk.verificationFailed',
  verification_link_token_expired: 'errors.clerk.verificationExpired',
  verification_missing: 'errors.clerk.verificationNotSent',
  verification_unknown_status: 'errors.clerk.verificationFailed',

  // Rate limiting / throttling
  rate_limit_exceeded: 'errors.clerk.rateLimited',

  // Account status errors
  user_not_found: 'errors.clerk.userNotFound',
  user_locked: 'errors.clerk.accountLocked',
  user_banned: 'errors.clerk.accountBanned',
  user_locked_indefinitely: 'errors.clerk.accountLocked',
  user_create_organization_not_enabled: 'errors.clerk.featureDisabled',
  user_delete_self_not_enabled: 'errors.clerk.featureDisabled',
  user_quota_exceeded: 'errors.clerk.accountLocked',

  // Session errors
  session_token_expired: 'errors.clerk.sessionExpired',
  session_token_invalid: 'errors.clerk.sessionInvalid',
  session_reverification_required: 'errors.clerk.sessionReverificationRequired',
  session_invalid: 'errors.clerk.sessionInvalid',
  session_idle_timeout: 'errors.clerk.sessionExpired',
  session_active_count_limit_reached: 'errors.clerk.sessionLimitReached',
  session_already_exists: 'errors.clerk.sessionExists',

  // Client/Resource errors
  client_not_found: 'errors.clerk.resourceNotFound',
  client_not_found_in_request: 'errors.clerk.resourceNotFound',
  resource_not_found: 'errors.clerk.resourceNotFound',
  resource_forbidden: 'errors.clerk.unauthorized',

  // Feature errors
  feature_not_enabled: 'errors.clerk.featureDisabled',
  feature_requires_dynamic_oauth_client_registration: 'errors.clerk.featureDisabled',
  feature_requires_oauth2_consent_screen_enabled: 'errors.clerk.featureDisabled',
  feature_requires_oidc_provider: 'errors.clerk.featureDisabled',

  // MFA/OTP/TOTP errors
  mfa_challenge_already_exists: 'errors.clerk.sessionExists',
  mfa_no_member_strategy: 'errors.clerk.verificationFailed',
  mfa_not_enabled: 'errors.clerk.featureDisabled',
  mfa_sms_phone_number_exists: 'errors.clerk.identifierExists',
  mfa_totp_exists: 'errors.clerk.featureDisabled',
  otp_invalid_code: 'errors.clerk.verificationFailed',
  otp_not_found: 'errors.clerk.verificationNotSent',
  totp_already_enabled: 'errors.clerk.featureDisabled',
  totp_invalid_code: 'errors.clerk.verificationFailed',
  totp_not_enabled: 'errors.clerk.featureDisabled',

  // URL errors
  invalid_url_scheme: 'errors.clerk.invalidUrl',

  // Generic fallbacks
  forbidden: 'errors.clerk.unauthorized',
  identifier_not_found: 'errors.clerk.invalidCredentials',
  identifier_exists: 'errors.clerk.identifierExists',
}

/**
 * Hook to get a localized error message from a Clerk error
 * Returns null if the error is not a Clerk error
 *
 * @returns A function that takes an error and returns a localized message or null
 */
export function useClerkErrorMessage() {
  const { t } = useTranslation('forms')

  return (error: unknown): string | null => {
    if (!isClerkAPIResponseError(error)) {
      if (error instanceof Error) {
        return error.message
      }
      return null
    }

    const firstError = error.errors?.[0]
    if (!firstError) {
      return null
    }

    console.error(`[Clerk error] ${firstError.code}`, firstError)

    const errorCode = firstError.code
    if (!errorCode) {
      return null
    }

    // Try to get a translated message for this error code
    const translationKey = clerkErrorCodeMap[errorCode]
    if (translationKey) {
      const translatedMessage = t(translationKey, { defaultValue: '' })
      if (translatedMessage) {
        if (firstError.meta && 'paramName' in firstError.meta) {
          const fieldName = firstError.meta.paramName as string

          // Map common field names to specific error messages
          const fieldErrorMap: Record<string, string> = {
            password: 'errors.clerk.passwordInvalid',
            email: 'errors.clerk.emailInvalid',
            identifier: 'errors.clerk.identifierInvalid',
          }

          const fieldErrorKey = fieldErrorMap[fieldName]
          if (fieldErrorKey) {
            const fieldMessage = t(fieldErrorKey, { defaultValue: '' })
            if (fieldMessage) {
              return fieldMessage
            }
          }
        }

        return translatedMessage
      }
    }

    // Fallback to the longMessage if translation exists but is empty
    return firstError.longMessage || firstError.message || null
  }
}
